import { bboxCenter } from "utils/math/bboxCenter";
import { ALL_STATUSES, STATUS_PAINT_ORDER } from "./constants";
import type {
  VisualDiffMarker,
  VisualDiffRoute,
  VisualDiffStatus,
} from "../utils/computeVisualDiff";
import type {
  CountItemsByStatusArgs,
  DiffNavigationTarget,
  SelectVisibleItemsArgs,
  StatusFilterableItem,
} from "./types";

export const buildAllVisibleStatuses = (): Record<
  VisualDiffStatus,
  boolean
> => ({
  added: true,
  removed: true,
  changed: true,
  unchanged: true,
});

export const selectVisibleItems = <TItem extends StatusFilterableItem>({
  itemsByStatus,
  visibleStatuses,
  selectedTypeKey,
}: SelectVisibleItemsArgs<TItem>): TItem[] => {
  const visibleItems: TItem[] = [];
  for (const status of STATUS_PAINT_ORDER) {
    if (!visibleStatuses[status]) continue;
    for (const item of itemsByStatus[status]) {
      const isSelectedType =
        selectedTypeKey === null || item.typeKey === selectedTypeKey;
      if (!isSelectedType) continue;
      visibleItems.push(item);
    }
  }
  return visibleItems;
};

// Deliberately ignores the legend toggles: a chip that hid its own count would drop to 0 and lock itself.
export const countItemsByStatus = <TItem extends StatusFilterableItem>({
  itemsByStatus,
  selectedTypeKey,
}: CountItemsByStatusArgs<TItem>): Record<VisualDiffStatus, number> => {
  const counts = { added: 0, removed: 0, changed: 0, unchanged: 0 };
  for (const status of ALL_STATUSES) {
    if (selectedTypeKey === null) {
      counts[status] = itemsByStatus[status].length;
      continue;
    }
    counts[status] = itemsByStatus[status].filter(
      (item) => item.typeKey === selectedTypeKey,
    ).length;
  }
  return counts;
};

export const buildMarkerNavigationTarget = (
  marker: VisualDiffMarker,
): DiffNavigationTarget => ({
  id: marker.id,
  zoomTargets: [bboxCenter({ bbox: marker.bbox })],
});

export const buildRouteNavigationTarget = (
  diffRoute: VisualDiffRoute,
): DiffNavigationTarget => ({
  id: diffRoute.route.id,
  // Both extents corners, so zooming frames the whole run and not just its middle.
  zoomTargets: [
    {
      x: diffRoute.route.coordinateExtents.minX,
      y: diffRoute.route.coordinateExtents.minY,
    },
    {
      x: diffRoute.route.coordinateExtents.maxX,
      y: diffRoute.route.coordinateExtents.maxY,
    },
  ],
});

// Already Hilbert-ordered by computeVisualDiff, so this only drops hidden statuses.
export const buildMarkerNavigationTargets = (
  markers: VisualDiffMarker[],
  visibleStatuses: Record<VisualDiffStatus, boolean>,
): DiffNavigationTarget[] =>
  markers
    .filter((marker) => visibleStatuses[marker.status])
    .map(buildMarkerNavigationTarget);

export const buildRouteNavigationTargets = (
  diffRoutes: VisualDiffRoute[],
  visibleStatuses: Record<VisualDiffStatus, boolean>,
): DiffNavigationTarget[] =>
  diffRoutes
    .filter((diffRoute) => visibleStatuses[diffRoute.status])
    .map(buildRouteNavigationTarget);
